import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ThemeProvider, createTheme, Theme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';

interface ThemeContextType {
  isDarkMode: boolean;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useThemeContext must be used within a ThemeContextProvider');
  }
  return context;
};

interface ThemeContextProviderProps {
  children: ReactNode;
}

export const ThemeContextProvider: React.FC<ThemeContextProviderProps> = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme-mode');
    return saved ? JSON.parse(saved) : true; // Default to dark mode
  });

  useEffect(() => {
    localStorage.setItem('theme-mode', JSON.stringify(isDarkMode));
  }, [isDarkMode]);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  const createAppTheme = (darkMode: boolean): Theme => {
    return createTheme({
      palette: {
        mode: darkMode ? 'dark' : 'light',
        primary: {
          main: darkMode ? '#00bcd4' : '#2563EB', // Cyan for dark, Professional Blue for light
          light: darkMode ? '#4dd0e1' : '#3b82f6',
          dark: darkMode ? '#0097a7' : '#1e40af',
        },
        secondary: {
          main: darkMode ? '#64b5f6' : '#0EA5E9', // Light blue for dark, Cyan-Blue for light
          light: darkMode ? '#90caf9' : '#38bdf8',
          dark: darkMode ? '#42a5f5' : '#0284c7',
        },
        background: {
          default: darkMode ? '#0a1929' : '#F8FAFC', // Deep navy vs Very light blue-gray
          paper: darkMode ? '#132f4c' : '#FFFFFF', // Navy vs Pure white
        },
        text: {
          primary: darkMode ? '#ffffff' : '#0F172A', // White vs Dark blue-gray
          secondary: darkMode ? '#b0bec5' : '#475569', // Light gray vs Medium gray
          disabled: darkMode ? '#78909c' : '#94A3B8', // Medium gray vs Light gray
        },
        divider: darkMode ? 'rgba(0, 188, 212, 0.2)' : '#E2E8F0', // Subtle gray for dividers
        info: {
          main: darkMode ? '#29b6f6' : '#0EA5E9',
          light: darkMode ? '#4fc3f7' : '#38bdf8',
          dark: darkMode ? '#0288d1' : '#0284c7',
        },
        success: {
          main: '#10B981', // Fresh green for both modes
          light: '#34d399',
          dark: '#059669',
          contrastText: '#FFFFFF',
        },
        warning: {
          main: '#F59E0B', // Warm orange for both modes
          light: '#fbbf24',
          dark: '#d97706',
          contrastText: '#FFFFFF',
        },
        error: {
          main: darkMode ? '#f44336' : '#ef4444',
          light: darkMode ? '#e57373' : '#f87171',
          dark: darkMode ? '#d32f2f' : '#dc2626',
        },
      },
      typography: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        h1: {
          fontSize: '2.5rem',
          fontWeight: 600,
          marginBottom: '1rem',
        },
        h2: {
          fontSize: '2rem',
          fontWeight: 500,
          marginBottom: '0.75rem',
        },
        h3: {
          fontSize: '1.5rem',
          fontWeight: 500,
          marginBottom: '0.5rem',
        },
      },
      components: {
        MuiButton: {
          styleOverrides: {
            root: {
              borderRadius: 12,
              textTransform: 'none',
              fontSize: '1rem',
              padding: '12px 24px',
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: darkMode
                  ? '0 8px 25px rgba(0, 188, 212, 0.3)'
                  : '0 8px 25px rgba(37, 99, 235, 0.25)',
              },
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              borderRadius: 16,
              boxShadow: darkMode
                ? '0 8px 32px rgba(0,0,0,0.4)'
                : '0 4px 20px rgba(37, 99, 235, 0.08)',
              border: darkMode
                ? '1px solid rgba(0, 188, 212, 0.2)'
                : '1px solid #E2E8F0',
              transition: 'all 0.3s ease-in-out',
              background: darkMode
                ? 'linear-gradient(135deg, #132f4c 0%, #1e3a8a 100%)'
                : '#FFFFFF',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: darkMode
                  ? '0 16px 48px rgba(0, 188, 212, 0.2)'
                  : '0 12px 40px rgba(37, 99, 235, 0.12)',
                border: darkMode
                  ? '1px solid rgba(0, 188, 212, 0.4)'
                  : '1px solid rgba(37, 99, 235, 0.3)',
              },
            },
          },
        },
        MuiContainer: {
          styleOverrides: {
            root: {
              paddingLeft: '24px',
              paddingRight: '24px',
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              backgroundImage: 'none',
            },
          },
        },
      },
    });
  };

  const theme = createAppTheme(isDarkMode);

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleTheme }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};